import { UserLevel } from "@/lib/levelingSystem";
import { createLogger } from "@/lib/logger";
import { useEffect, useRef, useState } from "react";

const logger = createLogger("user-level");

interface UseUserLevelResult {
  userLevel: UserLevel | null;
  loading: boolean;
  error: string | null;
  refreshLevel: () => void;
  lastRefresh: number;
}

export function useUserLevel(userEmail: string | null): UseUserLevelResult {
  const [userLevel, setUserLevel] = useState<UserLevel | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<number>(0);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchUserLevel = async () => {
    if (!userEmail) {
      setUserLevel(null);
      return;
    }

    // Prevent concurrent requests
    if (isRefreshing || loading) {
      return;
    }

    setLoading(true);
    setIsRefreshing(true);
    setError(null);

    try {
      const response = await fetch("/api/user-level", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userEmail }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user level: ${response.statusText}`);
      }

      const data = (await response.json()) as any;

      // Log successful level fetch (reduced logging)
      if (data.success && data.userLevel) {
        logger.debug("Level updated", {
          title: data.userLevel.title,
          memoryCount: data.memoryCount,
        });
      }

      if (data.success && data.userLevel) {
        setUserLevel(data.userLevel);
        setLastRefresh(Date.now());
      } else {
        throw new Error(data.error || "Failed to get user level data");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
      setError(errorMessage);
      logger.error("Error fetching user level", { error: errorMessage });
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const refreshLevel = () => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    // Add a small delay to ensure memories are saved before checking level
    debounceTimeoutRef.current = setTimeout(() => {
      fetchUserLevel();
    }, 3000); // 3 second delay
  };

  useEffect(() => {
    // Only fetch on initial mount or when userEmail actually changes
    if (userEmail && userEmail !== "tester") {
      fetchUserLevel();
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [userEmail]); // Only depend on userEmail, not on fetchUserLevel

  return {
    userLevel,
    loading,
    error,
    refreshLevel,
    lastRefresh,
  };
}
